import {
  CreditPlan,
  CreditPlanResultItem,
  CreditPlanType,
  ItemAnalyticsModel,
  ItemListAnalyticsModel
} from '@/@types/client-api';
import { ConvertItemAnalyticsModelBase } from '@/analytics/analyticsUtils';
import { AnalyticsContext, AnalyticsEventName } from '@/analytics/constants';
import { trackBulkRemoveWishListEvent, trackRemoveWishListEvent } from '@/analytics/ecommerce';
import { useAnalytics } from '@/analytics/hooks/useAnalytics';
import { Currency } from '@/constants/currency';
import {
  useRemoveAllFromLibraryMutation,
  useSaveAllToLibraryMutation
} from '@/redux/api/client/creditPlan';
import { useRemoveFromLibraryMutation } from '@/redux/api/client/library';
import { useCallback, useMemo, useState } from 'react';

type SaveMethod = 'save_all_button' | 'save_one';

type CreditPlanLibraryProps = {
  plan: CreditPlan;
  resolvedJurisdiction: string;
  itemListAnalyticsModel: ItemListAnalyticsModel;
  analyticsContext: AnalyticsContext;
  jurisdictionId?: string;
  onSavedItemsChange?: (records: Record<string, boolean>) => void;
  scopeKey?: string;
};

/**
 * Hook responsible for Credit Plan Library actions.
 * @param plan the credit plan
 * @param jurisdictionId
 * @param resolvedJurisdiction
 * @param itemListAnalyticsModel
 * @returns
 */
const useCreditPlanLibrary = ({
  plan,
  analyticsContext,
  jurisdictionId,
  resolvedJurisdiction,
  itemListAnalyticsModel,
  onSavedItemsChange,
  scopeKey = 'default'
}: CreditPlanLibraryProps) => {
  const { trackEvent } = useAnalytics();
  const [savingState, setSavingState] = useState<{
    scopeKey: string;
    items: Record<string, boolean>;
  }>({ scopeKey, items: {} });
  const savingItems = savingState.scopeKey === scopeKey ? savingState.items : {};

  const [saveAllToLibrary] = useSaveAllToLibraryMutation();
  const [removeAllToLibrary] = useRemoveAllFromLibraryMutation();
  const [removeSingleFromLibrary] = useRemoveFromLibraryMutation();

  const allPlanItems = useMemo(() => {
    const itemsByVariationPk = new Map<string, CreditPlanResultItem>();
    plan?.creditProductGroups
      ?.flatMap(group => group.searchResultItems ?? [])
      .forEach(item => {
        if (item.variationPk && !itemsByVariationPk.has(item.variationPk))
          itemsByVariationPk.set(item.variationPk, item);
      });
    return [...itemsByVariationPk.values()];
  }, [plan?.creditProductGroups]);

  // Event-level list metadata for add_to_wishlist. Item-level metadata comes from
  // itemListAnalyticsModel so it persists into the purchase stack (begin_checkout, purchase).
  const standardizedListId = useMemo(
    () =>
      `credit_plan_panel_${(jurisdictionId || resolvedJurisdiction).toLowerCase().replace(/\s+/g, '_')}`,
    [jurisdictionId, resolvedJurisdiction]
  );

  const standardizedListName = useMemo(
    () => `credit_plan_panel_${resolvedJurisdiction.toLowerCase().replace(/\s+/g, '_')}`,
    [resolvedJurisdiction]
  );

  const trackAddToWishlist = useCallback(
    (savedItems: CreditPlanResultItem[], saveMethod: SaveMethod) => {
      const items = savedItems
        .filter(item => !!item?.itemAnalyticsModel)
        .map(item =>
          ConvertItemAnalyticsModelBase(
            item.itemAnalyticsModel,
            itemListAnalyticsModel,
            allPlanItems.findIndex(planItem => planItem?.variationPk === item.variationPk)
          )
        );

      if (!items.length) return;

      const totalValue = items.map(a => a?.price || 0).reduce((acc, current) => acc + current, 0);

      trackEvent(AnalyticsEventName.AddToWishlist, {
        context: analyticsContext,
        ecommerce: {
          save_method: saveMethod,
          item_list_id: standardizedListId,
          item_list_name: standardizedListName,
          value: totalValue,
          currency: Currency.USD,
          items
        }
      });
    },
    [
      allPlanItems,
      analyticsContext,
      itemListAnalyticsModel,
      standardizedListId,
      standardizedListName,
      trackEvent
    ]
  );

  const toggleIsSaving = useCallback(
    (pks: string[], state: boolean) => {
      setSavingState(prev => {
        if (!state && prev.scopeKey !== scopeKey) return prev;

        const next = prev.scopeKey === scopeKey ? { ...prev.items } : {};
        pks.forEach(pk => {
          if (state) next[pk] = true;
          else delete next[pk];
        });
        return { scopeKey, items: next };
      });
    },
    [scopeKey]
  );

  const _saveToLibrary = useCallback(
    async (pks: string[], planType: CreditPlanType) => {
      if (!pks?.length || !jurisdictionId) return {};

      toggleIsSaving(pks, true);

      try {
        const { data, error } = await saveAllToLibrary({
          codes: [...new Set(pks)],
          license: jurisdictionId,
          creditPlanType: planType
        });

        const success = pks.every(k => !!data?.[k]) && !error;
        const savedItems = Object.fromEntries(pks.filter(pk => !!data?.[pk]).map(pk => [pk, true]));

        if (Object.keys(savedItems).length) onSavedItemsChange?.(savedItems);

        return { success, data };
      } finally {
        toggleIsSaving(pks, false);
      }
    },
    [jurisdictionId, onSavedItemsChange, saveAllToLibrary, toggleIsSaving]
  );

  const handleSaveAllToLibrary = useCallback(
    async (planType: CreditPlanType) => {
      const pks = allPlanItems?.map(a => a?.variationPk)?.filter((a): a is string => !!a) ?? [];

      if (!pks.length || !jurisdictionId) return false;

      // Track save_creditplan event
      trackEvent(AnalyticsEventName.SaveCreditPlan, {
        context: analyticsContext,
        jurisdiction: resolvedJurisdiction
      });

      // Track add_to_wishlist event with ecommerce data for every item in the plan
      trackAddToWishlist(allPlanItems, 'save_all_button');

      const { success, data } = await _saveToLibrary(pks, planType);

      return success && pks?.some(k => !!data?.[k]);
    },
    [
      allPlanItems,
      trackEvent,
      analyticsContext,
      resolvedJurisdiction,
      jurisdictionId,
      trackAddToWishlist,
      _saveToLibrary
    ]
  );

  const handleRemoveAllFromLibrary = useCallback(async () => {
    const pks = allPlanItems.map(a => a?.variationPk).filter((a): a is string => !!a);

    if (!pks?.length) return false;

    if (!jurisdictionId) return false;

    toggleIsSaving(pks, true);

    try {
      const { data, error } = await removeAllToLibrary({
        codes: pks,
        license: jurisdictionId
      });

      if (!error && data) {
        if (pks?.some(k => !!data?.[k])) {
          onSavedItemsChange?.(
            Object.fromEntries(pks.filter(pk => !!data[pk]).map(pk => [pk, false]))
          );

          const removedItems = allPlanItems.filter(
            item => !!item?.variationPk && !!data[item.variationPk]
          );
          const removedItemAnalytics = removedItems
            .map(item => item.itemAnalyticsModel)
            .filter((item): item is ItemAnalyticsModel => !!item);

          removedItemAnalytics.forEach(item => {
            trackRemoveWishListEvent(item, analyticsContext, true, itemListAnalyticsModel);
          });

          trackBulkRemoveWishListEvent(
            removedItemAnalytics,
            analyticsContext,
            itemListAnalyticsModel
          );

          return data;
        }
      }
      return;
    } finally {
      toggleIsSaving(pks, false);
    }
  }, [
    allPlanItems,
    jurisdictionId,
    toggleIsSaving,
    removeAllToLibrary,
    onSavedItemsChange,
    analyticsContext,
    itemListAnalyticsModel
  ]);

  const handleSingleSaveToLibrary = useCallback(
    async (planType: CreditPlanType, item?: CreditPlanResultItem | null) => {
      if (item?.variationPk) {
        const { success, data } = await _saveToLibrary([item?.variationPk], planType);

        if (success && data?.[item?.variationPk]) {
          trackAddToWishlist([item], 'save_one');
          return true;
        }
      }
      return false;
    },
    [_saveToLibrary, trackAddToWishlist]
  );

  const handleSingleRemoveFromLibrary = useCallback(
    async (item?: CreditPlanResultItem | null) => {
      if (item?.variationPk && jurisdictionId) {
        toggleIsSaving([item?.variationPk], true);
        try {
          const { data, error } = await removeSingleFromLibrary(item?.variationPk);

          if (!!data && !error) {
            onSavedItemsChange?.({ [item.variationPk]: false });

            trackRemoveWishListEvent(
              item.itemAnalyticsModel,
              analyticsContext,
              false,
              itemListAnalyticsModel
            );

            return true;
          }
        } finally {
          toggleIsSaving([item?.variationPk], false);
        }
      }
      return false;
    },
    [
      jurisdictionId,
      toggleIsSaving,
      removeSingleFromLibrary,
      onSavedItemsChange,
      analyticsContext,
      itemListAnalyticsModel
    ]
  );

  return {
    /** Save all Credit Plan items to the library. Correct metadata will be attached. */
    handleSaveAllToLibrary,
    /** Remove all Credit Plan items from the user's library. */
    handleRemoveAllFromLibrary,
    /**
     * Add a single Credit Plan item to the user's library.
     * @param item the Credit Plan program item
     */
    handleSingleSaveToLibrary,
    /**
     * Remove a single Credit Plan item from the user's library.
     * @param item the Credit Plan program item
     */
    handleSingleRemoveFromLibrary,
    savingItems
  };
};

export default useCreditPlanLibrary;
